/**
 * Internal-note reactions are stored as a map of emoji to the list of user ids
 * that reacted with it. These helpers read and toggle that map without mutating
 * the input, so both the optimistic store update and the note UI agree on the
 * exact shape the backend persists.
 */

export type NoteReactions = Record<string, string[]>

/** Quick-pick emojis offered when reacting to an internal note. */
export const REACTION_EMOJIS = ["👍", "❤️", "😂", "🎉", "👀", "✅"] as const

/** Normalize a raw metadata.reactions value to the note {emoji: userIds} shape. */
export function toNoteReactions(raw: unknown): NoteReactions {
  if (!raw || typeof raw !== "object") return {}
  const result: NoteReactions = {}
  for (const [emoji, value] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      result[emoji] = value.filter((id): id is string => typeof id === "string")
    }
  }
  return result
}

/** True when the given user has reacted with the given emoji. */
export function hasReacted(reactions: NoteReactions, emoji: string, userId: string): boolean {
  return (reactions[emoji] ?? []).includes(userId)
}

/**
 * Return a new reactions map with the user's reaction to `emoji` toggled: added
 * when absent, removed when present. An emoji left with no reactors is dropped.
 */
export function toggleNoteReaction(
  reactions: NoteReactions,
  emoji: string,
  userId: string
): NoteReactions {
  const next: NoteReactions = {}
  for (const [key, ids] of Object.entries(reactions)) {
    next[key] = [...ids]
  }
  const reactors = next[emoji] ?? []
  if (reactors.includes(userId)) {
    const remaining = reactors.filter(id => id !== userId)
    if (remaining.length) {
      next[emoji] = remaining
    } else {
      delete next[emoji]
    }
  } else {
    next[emoji] = [...reactors, userId]
  }
  return next
}
